/**
 * GroupComparePanel — "Confronta ETF": the structural reading of a group of
 * 2 to 10 ETFs on one dimension (Phase 3).
 *
 * Deliberately weight-free: it answers "what do these ETFs share", not "how
 * much of my portfolio is duplicated" — that is the Portafoglio level.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, AlertTriangle } from "lucide-react";
import { SectionCard } from "@/components/system/SectionCard";
import { KpiCard } from "@/components/system/KpiCard";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_ETFS = 10;

const pct = (value, digits = 1) =>
  value === null || value === undefined ? "n/d" : `${Number(value).toFixed(digits)}%`;

export function GroupComparePanel({ sessionId, dimension, subjects, onSelectPair }) {
  const [selected, setSelected] = useState(() => subjects.slice(0, 3).map((s) => s.key));
  const [minShared, setMinShared] = useState("2");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const enough = selected.length >= 2;

  useEffect(() => {
    if (!sessionId || !enough) { setData(null); return undefined; }
    let cancelled = false;
    setLoading(true);
    setError(null);
    axios
      .get(`${API}/diversification/group/${sessionId}`, {
        params: { etfs: selected.join(","), dimension, min_shared: Number(minShared) },
      })
      .then(({ data: payload }) => { if (!cancelled) setData(payload); })
      .catch(() => {
        if (!cancelled) {
          setData(null);
          setError("Non è stato possibile confrontare gli ETF selezionati.");
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [sessionId, dimension, selected, minShared, enough]);

  const toggle = (key) => {
    setSelected((prev) => {
      if (prev.includes(key)) return prev.filter((k) => k !== key);
      if (prev.length >= MAX_ETFS) return prev;
      return [...prev, key];
    });
  };

  return (
    <div className="space-y-4" data-testid="group-compare-panel">
      <SectionCard
        testId="group-selection"
        title="Confronta ETF"
        description={`Scegli da 2 a ${MAX_ETFS} ETF dell'analisi: il confronto è strutturale e non usa i pesi del portafoglio.`}
        density="compact"
      >
        <div className="space-y-3">
          <div className="flex flex-wrap gap-x-4 gap-y-2">
            {subjects.map((subject) => {
              const checked = selected.includes(subject.key);
              return (
                <label
                  key={subject.key}
                  className="inline-flex items-center gap-2 min-h-10 text-label text-content cursor-pointer"
                  data-testid={`group-etf-${subject.key}`}
                >
                  <Checkbox
                    checked={checked}
                    onCheckedChange={() => toggle(subject.key)}
                    disabled={!checked && selected.length >= MAX_ETFS}
                    aria-label={`Includi ${subject.key} nel confronto`}
                  />
                  <span className="font-mono">{subject.key}</span>
                  <span className="text-content-muted">cov. {subject.coverage_pct.toFixed(0)}%</span>
                </label>
              );
            })}
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-[200px]">
              <label htmlFor="group-min-shared" className="block text-label text-content-muted mb-1">
                Presenti in almeno
              </label>
              <Select value={minShared} onValueChange={setMinShared}>
                <SelectTrigger id="group-min-shared" className="h-10" data-testid="group-min-shared">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: Math.max(1, selected.length - 1) }, (_, i) => i + 2).map((n) => (
                    <SelectItem key={n} value={String(n)} data-testid={`group-min-shared-${n}`}>
                      {n} ETF
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <p className="text-label text-content-muted" data-testid="group-selection-count">
              {selected.length} ETF selezionati
              {selected.length >= MAX_ETFS && ` (massimo ${MAX_ETFS})`}
            </p>
          </div>
          {!enough && (
            <p className="text-label text-warning" data-testid="group-need-two">
              Seleziona almeno 2 ETF per confrontarli.
            </p>
          )}
        </div>
      </SectionCard>

      {loading && (
        <div className="flex items-center gap-2 text-sm text-content-muted" data-testid="group-loading">
          <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
          Confronto in corso…
        </div>
      )}
      {error && !loading && (
        <p className="text-sm text-danger" data-testid="group-error">{error}</p>
      )}

      {data && !loading && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <KpiCard
              testId="group-mean-overlap"
              label="Overlap medio"
              value={pct(data.mean_overlap_pct)}
              density="compact"
              unavailable={data.mean_overlap_pct == null}
              unavailableReason="nessuna coppia misurabile"
            />
            <KpiCard
              testId="group-max-pair"
              label="Coppia più sovrapposta"
              value={data.max_pair
                ? `${data.max_pair.a}·${data.max_pair.b} ${pct(data.max_pair.classified_overlap_pct)}`
                : "n/d"}
              density="compact"
              unavailable={!data.max_pair}
              unavailableReason="nessuna coppia misurabile"
            />
            <KpiCard
              testId="group-min-pair"
              label="Coppia meno sovrapposta"
              value={data.min_pair
                ? `${data.min_pair.a}·${data.min_pair.b} ${pct(data.min_pair.classified_overlap_pct)}`
                : "n/d"}
              density="compact"
              unavailable={!data.min_pair}
              unavailableReason="nessuna coppia misurabile"
            />
            <KpiCard
              testId="group-unique-count"
              label="Esposizioni del gruppo"
              value={String(data.unique_exposures_count)}
              density="compact"
            />
          </div>

          {(data.warnings || []).map((warning) => (
            <p
              key={warning}
              role="status"
              data-testid="group-warning"
              className="flex items-start gap-2 text-label text-warning"
            >
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" aria-hidden="true" />
              {warning}
            </p>
          ))}

          <div className="grid gap-3 lg:grid-cols-3">
            <ExposureList
              testId="group-in-all"
              title="In tutti gli ETF"
              total={data.in_all_count}
              items={data.in_all}
              emptyText="Nessuna esposizione è presente in tutti gli ETF selezionati."
            />
            <ExposureList
              testId="group-in-at-least"
              title={`In almeno ${data.min_shared} ETF`}
              total={data.in_at_least_count}
              items={data.in_at_least}
              emptyText="Nessuna esposizione raggiunge questa diffusione."
            />
            <ExposureList
              testId="group-unique-to-one"
              title="Solo in un ETF"
              total={data.unique_to_one_count}
              items={data.unique_to_one}
              emptyText="Ogni esposizione del gruppo è condivisa da almeno due ETF."
            />
          </div>

          <SectionCard
            testId="group-pairs"
            title="Coppie del gruppo"
            description="Sovrapposizione strutturale coppia per coppia: apri il dettaglio per capire da dove viene."
            density="compact"
          >
            <div className="overflow-x-auto rounded-lg border border-line">
              <table className="w-full text-label" data-testid="group-pairs-table">
                <thead>
                  <tr className="bg-surface-subtle text-content-muted">
                    <th scope="col" className="px-3 py-2 text-left font-medium">Coppia</th>
                    <th scope="col" className="px-3 py-2 text-right font-medium">Sovrapposizione</th>
                    <th scope="col" className="px-3 py-2 text-right font-medium">Dettaglio</th>
                  </tr>
                </thead>
                <tbody>
                  {data.pairs.map((pair) => (
                    <tr
                      key={`${pair.a}|${pair.b}`}
                      className="border-t border-line"
                      data-testid={`group-pair-${pair.a}-${pair.b}`}
                    >
                      <td className="px-3 py-2 font-mono text-content">{pair.a} · {pair.b}</td>
                      <td className="px-3 py-2 text-right tabular">
                        {pair.supported && pair.classified_overlap_pct != null
                          ? pct(pair.classified_overlap_pct)
                          : "n/d"}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <button
                          type="button"
                          data-testid={`group-pair-open-${pair.a}-${pair.b}`}
                          onClick={() => onSelectPair({ a: pair.a, b: pair.b })}
                          aria-label={`Apri il dettaglio di ${pair.a} e ${pair.b}`}
                          className="min-h-10 px-3 rounded-md border border-line text-content
                                     transition-colors hover:bg-surface-subtle
                                     focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
                        >
                          Apri
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </SectionCard>
        </>
      )}
    </div>
  );
}

function ExposureList({ title, items, total, emptyText, testId }) {
  return (
    <SectionCard title={title} density="compact" testId={testId}>
      {items.length === 0 ? (
        <p className="text-label text-content-muted" data-testid={`${testId}-empty`}>{emptyText}</p>
      ) : (
        <>
          <ul className="space-y-1">
            {items.map((item) => (
              <li
                key={item.key}
                className="flex flex-wrap items-baseline justify-between gap-2 text-label border-b border-line py-1"
                data-testid={`${testId}-${item.key}`}
              >
                <span className="text-content">
                  {item.label}
                  <span className="text-content-muted"> · {item.etf_count} ETF</span>
                </span>
                <span className="tabular text-content-secondary">
                  peso medio {pct(item.mean_weight_pct, 2)}
                </span>
              </li>
            ))}
          </ul>
          {total > items.length && (
            <p className="text-label text-content-muted mt-2" data-testid={`${testId}-more`}>
              Mostrate {items.length} esposizioni su {total}.
            </p>
          )}
        </>
      )}
    </SectionCard>
  );
}

export default GroupComparePanel;
